import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { Media } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb, useMedia } from '@/features/media';

import type { PostMediaCandidate } from '../hooks/use-post-product-media';
import { MAX_POST_MEDIA } from '../schemas/post-form-schema';

export interface MediaStripProps {
  /** Chosen image ids, in slide order. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** Images of the chosen products, to pick from. */
  candidates: PostMediaCandidate[];
  lookup: ReadonlyMap<string, Media>;
  /** Rendition URL per chosen image, null until the backend has rendered it. */
  renditions: ReadonlyMap<string, string | null>;
  loading: boolean;
  error: unknown;
  onRetry?: () => void;
  disabled?: boolean;
  /** Nothing to pick from yet (no product chosen). */
  noProducts: boolean;
}

function Thumb({
  mediaId,
  known,
  rendition,
  className,
}: {
  mediaId: string;
  known: Media | undefined;
  rendition: string | null | undefined;
  className?: string;
}) {
  const { data } = useMedia(known || rendition ? null : mediaId);
  if (rendition) {
    return (
      <div
        className={cn(
          'bg-muted flex aspect-square items-center justify-center overflow-hidden rounded-md',
          className,
        )}
      >
        <img
          src={rendition}
          alt={known?.alt ?? ''}
          className="size-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  return <MediaThumb media={known ?? data} rendition="medium" className={className} />;
}

/**
 * The slides of the pin, one to five, in order, with the chosen products' galleries under them
 * to add from. Reorder is by buttons (keyboard first; no drag library in the admin).
 */
export function MediaStrip({
  value,
  onChange,
  candidates,
  lookup,
  renditions,
  loading,
  error,
  onRetry,
  disabled = false,
  noProducts,
}: MediaStripProps) {
  const { t } = useTranslation('social');
  const full = value.length >= MAX_POST_MEDIA;
  const chosen = new Set(value);

  const move = (index: number, delta: number) => {
    const next = [...value];
    const target = index + delta;
    const current = next[index];
    const other = next[target];
    if (current === undefined || other === undefined) return;
    next[index] = other;
    next[target] = current;
    onChange(next);
  };
  const remove = (id: string) => onChange(value.filter((v) => v !== id));
  const add = (id: string) => {
    if (full || chosen.has(id)) return;
    onChange([...value, id]);
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium">{t('editor.media.slides')}</span>
          <span className="text-muted-foreground text-xs tabular-nums">
            {t('editor.media.count', { count: value.length, max: MAX_POST_MEDIA })}
          </span>
        </div>
        {value.length === 0 ? (
          <p className="text-muted-foreground rounded-md border border-dashed p-4 text-center text-sm">
            {t('editor.media.empty')}
          </p>
        ) : (
          <ol className="flex flex-wrap gap-3" aria-label={t('editor.media.slides')}>
            {value.map((id, index) => {
              const rendition = renditions.get(id);
              return (
                <li
                  key={id}
                  className="group relative flex w-28 flex-col gap-1"
                  aria-label={t('editor.media.slide', { n: index + 1 })}
                >
                  <div className="relative">
                    <Thumb
                      mediaId={id}
                      known={lookup.get(id)}
                      rendition={rendition}
                      className="w-28 border"
                    />
                    <span className="bg-background/90 absolute top-1 left-1 rounded px-1.5 text-xs font-medium tabular-nums">
                      {index + 1}
                    </span>
                    {!disabled ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon-xs"
                        className="absolute top-1 right-1"
                        aria-label={t('editor.media.remove', { n: index + 1 })}
                        onClick={() => remove(id)}
                      >
                        <X aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                  {!disabled ? (
                    <div className="flex justify-between">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={t('editor.media.moveEarlier', { n: index + 1 })}
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        <ChevronLeft aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={t('editor.media.moveLater', { n: index + 1 })}
                        disabled={index === value.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        <ChevronRight aria-hidden />
                      </Button>
                    </div>
                  ) : null}
                  {rendition === null ? (
                    <span className="text-muted-foreground text-center text-xs">
                      {t('editor.media.rendering')}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {!disabled ? (
        <div>
          <span className="mb-2 block text-sm font-medium">{t('editor.media.fromProducts')}</span>
          {noProducts ? (
            <p className="text-muted-foreground text-sm">{t('editor.media.pickProductsFirst')}</p>
          ) : error ? (
            <ErrorState compact error={error} onRetry={onRetry} />
          ) : loading && candidates.length === 0 ? (
            <div className="flex gap-3">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="size-20" />
              ))}
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('editor.media.noProductImages')}</p>
          ) : (
            <ul className="flex flex-wrap gap-3" aria-label={t('editor.media.fromProducts')}>
              {candidates.map(({ media }) => {
                const picked = chosen.has(media.id);
                return (
                  <li key={media.id}>
                    <button
                      type="button"
                      className={cn(
                        'focus-visible:ring-ring/50 relative block rounded-md outline-none focus-visible:ring-[3px]',
                        picked && 'opacity-50',
                      )}
                      aria-label={
                        picked
                          ? t('editor.media.alreadyAdded', {
                              name: media.alt ?? media.filename ?? media.id,
                            })
                          : t('editor.media.add', { name: media.alt ?? media.filename ?? media.id })
                      }
                      aria-pressed={picked}
                      disabled={picked || full}
                      onClick={() => add(media.id)}
                    >
                      <MediaThumb media={media} rendition="medium" className="size-20 border" />
                      {!picked && !full ? (
                        <span className="bg-background/90 absolute right-1 bottom-1 rounded-full p-0.5">
                          <Plus className="size-3" aria-hidden />
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {full ? (
            <p className="text-muted-foreground mt-2 text-xs">
              {t('editor.media.full', { max: MAX_POST_MEDIA })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
